import { usePageCrumbs } from '../lib/crumbs.ts';
import { WalletMinimal } from '@orgus/ui/icons';
import type { ComponentType, ReactNode } from 'react';
import { Navigate, useParams } from 'react-router';
import { DataGate, PageBody } from '../components/shell/PageBody.tsx';
import { EmptyState } from '@orgus/ui';
import { SessionDrawerHost } from '../features/sessions/SessionDrawer.tsx';
import { SpendSkeleton, SpendView } from '../features/costs/SpendView.tsx';
import { useCosts } from '../features/costs/useCosts.ts';
import { ExportsView } from '../features/finance/ExportsView.tsx';
import { LedgerSkeleton, LedgerView } from '../features/finance/LedgerView.tsx';
import { ModelMixView } from '../features/finance/ModelMixView.tsx';
import { PnlView } from '../features/finance/PnlView.tsx';
import { ReconcileView } from '../features/finance/ReconcileView.tsx';
import { ReportSkeleton, SpendTabs } from '../features/finance/Scope.tsx';
import { WhenRuns } from '../features/server/ModuleAbsent.tsx';

function SpendBody() {
  const costs = useCosts();
  if (!costs) {
    return (
      <div className="rounded-lg border border-border bg-surface">
        <EmptyState
          size="page"
          icon={<WalletMinimal />}
          title="This hub does not report spend"
          description="The hub answered, but without cost data, so it runs an Orgus version from before Spend. Restart the hub on the current Orgus version; spend then appears for every session it watches."
        />
      </div>
    );
  }
  return <SpendView costs={costs} />;
}

/** The finance views under Spend: /spend/<view>. Overview is the token spend page itself. */
const VIEWS: Record<string, { body: ComponentType; skeleton: ReactNode }> = {
  '': { body: SpendBody, skeleton: <SpendSkeleton /> },
  pnl: { body: PnlView, skeleton: <ReportSkeleton /> },
  reconcile: { body: ReconcileView, skeleton: <ReportSkeleton tiles={4} /> },
  ledger: { body: LedgerView, skeleton: <LedgerSkeleton /> },
  'model-mix': { body: ModelMixView, skeleton: <ReportSkeleton tiles={3} /> },
  exports: { body: ExportsView, skeleton: <ReportSkeleton tiles={0} /> },
};

const VIEW_LABEL: Record<string, string> = { pnl: 'P&L', reconcile: 'Reconciliation', ledger: 'Ledger', 'model-mix': 'Model mix', exports: 'Exports' };

/**
 * Spend, and the finance and cost portal behind its tabs: token spend with budgets and a
 * forecast (Overview), the project P&L, billing reconciliation, the ledger of other costs,
 * the model mix with a what-if, and exports.
 */
export default function Spend() {
  const params = useParams();
  const view = (params['*'] ?? '').split('/')[0] ?? '';
  const v = VIEWS[view];
  usePageCrumbs(v && view ? [{ label: VIEW_LABEL[view] ?? view }] : []);
  if (!v) return <Navigate to="/spend" replace />;
  const Body = v.body;
  return (
    <PageBody>
      <SpendTabs />
      <WhenRuns module="finance" feature="Spend reports" plural instead="Overview shows what each session, model and machine costs today." size="page">
        <DataGate skeleton={v.skeleton}>
          <Body />
          <SessionDrawerHost />
        </DataGate>
      </WhenRuns>
    </PageBody>
  );
}
